import { AnyAction, createDraftSafeSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { push } from 'connected-react-router';
import { mergeDeepRight } from 'ramda';
import { ActionsObservable, combineEpics } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, filter, mergeMap } from 'rxjs/operators';

import { actions as notificationsActions } from '../notifications/notificationsSlice';
import { actions as pageActions } from '../page/pageSlice';
import { extractPaginationValues, getPaginationQueryParams } from '../page/pageUtils';
import { actions as sharedActions } from '../sharedSlice';

import { api, appDetailRoutes } from '~constants';
import { FireHydrant, PaginationRequestPayload, PaginationResponse, ResponseError } from '~models';
import { http } from '~services';
import { findDataByCallback, getResponseError, getResponsePayload, updateArray, updateArrayWithCallback } from '~utils';

export const updateInfo = (fireHydrant: FireHydrant, updates: Partial<FireHydrant>) =>
  mergeDeepRight(fireHydrant, updates) as FireHydrant;
export interface FireHydrantsState {
  data: FireHydrant[];
  loading: {
    list: boolean;
    create: boolean;
    details: boolean;
    edit: boolean;
    delete: boolean;
  };
  error: ResponseError | null;
}

export const initialState: FireHydrantsState = {
  data: [],
  loading: {
    list: false,
    create: false,
    details: false,
    edit: false,
    delete: false,
  },
  error: null,
};

export const { name, actions, reducer } = createSlice({
  name: 'fireHydrants',
  initialState,
  reducers: {
    // Fetch fire Hydrants list
    fetchFireHydrantsListInit: (
      state,
      payload: PayloadAction<PaginationRequestPayload & { businessUnitId: FireHydrant['buId'] }>
    ) => {
      state.error = null;
      state.loading.list = true;
    },
    fetchFireHydrantsListSuccess: (
      state,
      { payload: { content, number } }: PayloadAction<PaginationResponse<FireHydrant>>
    ) => {
      state.loading.list = false;

      state.data = content;
    },
    fetchFireHydrantsListFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.list = false;
      state.error = payload;
    },
    // fetch Fire Hydrant  by id
    fetchFireHydrantByIdInit: (
      state,
      { payload }: PayloadAction<{ businessUnitId: number; id: FireHydrant['id'] }>
    ) => {
      state.error = null;
      state.loading.details = true;
    },
    fetchFireHydrantByIdSuccess: (state, { payload }: PayloadAction<FireHydrant>) => {
      state.data = updateArray(state.data, payload);
      state.loading.details = false;
    },
    fetchFireHydrantByIdFailed: (state, action: PayloadAction<ResponseError>) => {
      state.loading.details = false;
      state.error = action.payload;
    },

    // create Fire Hydrant
    createFireHydrantInit: (
      state,
      { payload }: PayloadAction<{ businessUnitId: number; fireHydrant: Partial<FireHydrant> }>
    ) => {
      state.error = null;
      state.loading.create = true;
    },
    createFireHydrantSuccess: state => {
      state.loading.create = false;
    },
    createFireHydrantFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.create = false;
      state.error = payload;
    },

    // update Fire Hydrant
    updateFireHydrantInit: (
      state,
      { payload }: PayloadAction<{ businessUnitId: number; fireHydrant: Partial<FireHydrant> }>
    ) => {
      state.error = null;
      state.loading.edit = true;
    },
    updateFireHydrantSuccess: (state, { payload }: PayloadAction<FireHydrant>) => {
      state.loading.edit = false;
      const fireHydrant = findDataByCallback<FireHydrant>(
        state.data,
        fireHydrant => fireHydrant.id === payload?.id && fireHydrant.buId === payload.buId
      ) as FireHydrant;

      if (fireHydrant) {
        state.data = updateArrayWithCallback(
          state.data,
          updateInfo(fireHydrant, payload),
          (e, u) => e.id === payload?.id && u.buId === payload.buId
        );
      }
    },
    updateFireHydrantFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.edit = false;
      state.error = payload;
    },

    // delete Fire Hydrant
    deleteFireHydrantInit: (
      state,
      { payload }: PayloadAction<{ businessUnitId: FireHydrant['buId']; id: FireHydrant['id'] }>
    ) => {
      state.error = null;
      state.loading.delete = true;
    },
    deleteFireHydrantSuccess: (state, { payload }: PayloadAction<FireHydrant['id']>) => {
      state.loading.delete = false;
    },
    deleteFireHydrantFailed: (state, { payload }: PayloadAction<ResponseError>) => {
      state.loading.delete = false;
      state.error = payload;
    },

    // Reset List
    resetFireHydrants(state) {
      Object.assign(state, initialState);
    },

  },
  extraReducers: {
    [sharedActions.reset.toString()]: state => Object.assign(state, initialState),
  },
});

const getFireHydrantsState = (state: AES.RootState) => state.fireHydrants;

export const selectors = {
  getFireHydrantsState,

  getFireHydrantsList: createDraftSafeSelector(getFireHydrantsState, state => state.data),
  getFireHydrantsLoading: createDraftSafeSelector(getFireHydrantsState, state => state.loading),
  getFireHydrantsError: createDraftSafeSelector(getFireHydrantsState, state => state.error),
  getFireHydrantById: (id: FireHydrant['id'], buId: FireHydrant['buId']) =>
    createDraftSafeSelector(
      getFireHydrantsState,
      state => state.data.find(fireHydrant => fireHydrant.id === id && fireHydrant.buId === buId) as FireHydrant
    ),
};

const fetchFireHydrantsListEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(action => actions.fetchFireHydrantsListInit.match(action)),
    mergeMap(({ payload }) => {
      const searchParams = getPaginationQueryParams(payload);

      return http
        .getJSON<PaginationResponse<FireHydrant>>(api.fireHydrants.all(payload.businessUnitId as number, searchParams))
        .pipe(
          mergeMap(res =>
            of(actions.fetchFireHydrantsListSuccess(res), pageActions.setPagePagination(extractPaginationValues(res)))
          ),
          catchError(err => {
            const error = getResponseError(err);

            return of(actions.fetchFireHydrantsListFailed(error));
          })
        );
    })
  );

const fetchFireHydrantByIdEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.fetchFireHydrantByIdInit.match),
    mergeMap(({ payload: { businessUnitId, id } }) =>
      http.getJSON<FireHydrant>(api.fireHydrants.byId(businessUnitId, id)).pipe(
        mergeMap(values => of(actions.fetchFireHydrantByIdSuccess(values), pageActions.setPageFound())),
        catchError(err =>
          of(actions.fetchFireHydrantByIdFailed(getResponseError(err)), pageActions.setPageNotFound())
        )
      )
    )
  );

const createFireHydrantEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.createFireHydrantInit.match),
    mergeMap(({ payload: { businessUnitId, fireHydrant } }) =>
      http.post(api.fireHydrants.all(businessUnitId), fireHydrant).pipe(
        mergeMap(() =>
          of(actions.createFireHydrantSuccess(), push(appDetailRoutes.businessUnitFireHydrants(businessUnitId)))
        ),
        catchError(err => of(actions.createFireHydrantFailed(getResponseError(err))))
      )
    )
  );

const updateFireHydrantEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateFireHydrantInit.match),
    mergeMap(({ payload: { businessUnitId, fireHydrant } }) =>
      http.patch(api.fireHydrants.byId(businessUnitId as number, fireHydrant?.id as number), fireHydrant).pipe(
        mergeMap(values =>
          of(
            actions.updateFireHydrantSuccess(getResponsePayload(values)),
            push(appDetailRoutes.businessUnitFireHydrantDetails(businessUnitId as number, fireHydrant?.id as number)),
            actions.fetchFireHydrantByIdInit({
              businessUnitId: businessUnitId as number,
              id: fireHydrant?.id as number
            }),
            notificationsActions.enqueue({
              message: 'Fire Hydrant updated',
              options: { variant: 'success' },
            })
          )
        ),
        catchError(err => of(actions.updateFireHydrantFailed(getResponseError(err))))
      )
    )
  );

const deleteFireHydrantEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.deleteFireHydrantInit.match),
    mergeMap(({ payload: { businessUnitId, id } }) =>
      http.delete(api.fireHydrants.byId(businessUnitId as number, id)).pipe(
        mergeMap(() =>
          of(
            actions.deleteFireHydrantSuccess(id),
            push(appDetailRoutes.businessUnitFireHydrants(businessUnitId as number))
          )
        ),
        catchError(err => of(actions.deleteFireHydrantFailed(getResponseError(err))))
      )
    )
  );

export const epics = combineEpics(
  fetchFireHydrantsListEpic,
  createFireHydrantEpic,
  fetchFireHydrantByIdEpic,
  updateFireHydrantEpic,
  deleteFireHydrantEpic
);
export const allEpics = {
  fetchFireHydrantsListEpic,
  createFireHydrantEpic,
  fetchFireHydrantByIdEpic,
  updateFireHydrantEpic,
  deleteFireHydrantEpic,
};

declare global {
  namespace AES {
    export interface Actions {
      fireHydrants: typeof actions;
    }
    export interface RootState {
      fireHydrants: FireHydrantsState;
    }

    export interface Selectors {
      fireHydrants: typeof selectors;
    }
  }
}
